(async () => {
    const { Core } = await importModule('Core');
    const {
        arrEq,
        $JSON,
        cache,
        fetchHTML
    } = await importModule('Utils');


    const CN_UNAVAILABLE = '!unavailable';
    const CN_SOLD_OUT = '!sold-out';
    const ATTR_UNAVAILABLE = 'unavailable';
    const ATTR_OPTION_INDEX = 'data-option-index';
    const ATTR_VARIANT_ID = 'variant-id';
    const ATTR_SELLING_PLAN_ID = 'selling-plan-id';
    const ATTR_PRODUCT_URL = 'product-url';
    const ATTR_SMART_SELECT = 'smart-select';

    customElements.define('product-variant-selector', class extends Core {

        elements = {
            variantsJSON: '[data-variants-json]',
            optionsJSON: '[data-options-json]',
            inventoryJSON: '[data-inventory-json]',
            displayValues: '*[data-display-value]',
            selectors: '*[data-option-selector]',
            variantIdInput: 'input[name="id"]'
        }

        subscriptions = {
            'selling-plan:change': '_onSellingPlanChange',
            'cart:add': '_onCartAdd',
            'cart:change': '_onCartChange',
        }

        render() {
            this.options = $JSON(this.$optionsJSON);
            this.variants = $JSON(this.$variantsJSON);
            this.variantsInventory = $JSON(this.$inventoryJSON);

            if (!this.variants || this.variants.length === 0) {
                this.$variantIdInput.disabled = true;
                this.$selectors?.forEach(sel => sel.disabled = true);
                this.classList.add(CN_UNAVAILABLE);
                return;
            }

            this._handleCartQty();
            this._handleSellingPlanChange();
            this._getInitialVariant();

            this._handleOptionChange();

            if (this.variant) {
                this.$variantIdInput.value = this.variant.id;
                this.$variantIdInput.disabled = false;
            } else {
                this.$variantIdInput.disabled = true;
            }
        }

        _handleCartQty() {
            this.variants.map(variant => {
                if (this.variantsInventory[variant.id]) {
                    variant.available_to_add = +this.variantsInventory[variant.id].available_to_add;
                    variant.inventory_quantity = +this.variantsInventory[variant.id].inventory_quantity;
                }
            })
            this.subscribe('cart:add', { global: true });
            this.subscribe('cart:change', { global: true });
        }

        _onCartAdd({ items }) {
            if (!this.variants || !this.variant) {
                return;
            }
            items.forEach(addedItem => {
                const matchedVariant = this.variants.find(variant => variant.id === +addedItem.id);
                if (matchedVariant) {
                    matchedVariant.available_to_add -= +addedItem.quantity;
                    if (this.variant && matchedVariant.id === this.variant.id && matchedVariant.available_to_add <= 0) {
                        this._updateCurrent();
                    }
                }
            })
        }


        async _onCartChange(item) {
            if (!this.variants || !this.variant) {
                return;
            }
            const matchedVariant = this.variants.find(variant => variant.id === +item.id);
            if (matchedVariant && matchedVariant.inventory_quantity) {
                const oldAvailableToAdd = matchedVariant.available_to_add
                const newAvailableToAdd = matchedVariant.inventory_quantity - +item.quantity;
                matchedVariant.available_to_add = newAvailableToAdd;

                if (this.variant && matchedVariant.id === this.variant.id) {
                    if (oldAvailableToAdd <= 0 && newAvailableToAdd > 0 || newAvailableToAdd <= 0) {
                        this._updateCurrent();
                    }
                    return
                }
                this._clearVariantCache(matchedVariant.id, item.selling_plan);

            }
        }

        async _updateCurrent() {
            if (!this.variant) return
            if (this._updateTimeout) clearTimeout(this._updateTimeout);
            this._updateTimeout = setTimeout(async () => {
                await this._fetchVariantDOM();
                this._setOptionsAvailability();
                this._publishChanges();
            }, 500);
        }

        _clearVariantCache(variantId, sellingPlanId) {
            cache.set(`${variantId}${sellingPlanId || ''}`, undefined);
        }

        _handleSellingPlanChange() {
            if (this.hasAttribute(ATTR_SELLING_PLAN_ID)) {
                this.sellingPlanId = this.getAttribute(ATTR_SELLING_PLAN_ID);
                this.subscribe('selling-plan:change');
            } else {
                this.sellingPlanId = undefined;
            }
        }

        _handleOptionChange() {
            this._setOptionsAvailability();
            this.addEventListener('change', this.optionChangeHandler.bind(this));

        }

        _getInitialVariant() {
            this.variant = null;
            this.currentOptions = [];

            if (!this.variants || this.variants.length === 0) return

            const variantIdAttr = this.getAttribute(ATTR_VARIANT_ID);
            const initialVariantId = variantIdAttr ? +variantIdAttr : null;

            if (initialVariantId) {
                this.variant = this.variants.find(({ id }) => id === initialVariantId);
                if (this.variant) {
                    this.currentOptions = [...this.variant.options];
                    this.$variantIdInput.value = this.variant.id;
                    return;
                }
            }

            // Initialize empty currentOptions array with correct length based on option count
            if (this.options && this.options.length > 0) {
                this.currentOptions = new Array(this.options.length).fill("");
            }
        }

        _setOptionsAvailability() {
            if (!this.options || !this.variants) return;

            this.options
                .reduce(this._getOptionsState.bind(this), [])
                .forEach(this._toggleOptionAvailability.bind(this));

            // Mark option components as loaded
            this._markOptionsLoaded();
        }

        _markOptionsLoaded() {
            const radioComponent = this.querySelector('product-options-radio');
            const dropdownComponent = this.querySelector('product-options-dropdown');

            if (radioComponent) {
                radioComponent.classList.add('!loaded');
            }
            if (dropdownComponent) {
                dropdownComponent.classList.add('!loaded');
            }
        }

        optionChangeHandler(e) {
            if (
                !(e instanceof CustomEvent) &&
                e.target &&
                e.target.matches(`[${ATTR_OPTION_INDEX}]`) &&
                e.target.type !== 'radio'
            ) {
                const target = e.target;
                const optionIndex = +target.getAttribute(ATTR_OPTION_INDEX);
                const newValue = target.value;

                this.currentOptions[optionIndex] = newValue;

                this._updateDisplayValue(target);

                if (this.smartSelect) {
                    this._updateVariantWithOption(target);
                    this._setOptionsAvailability();
                    this._publishChanges();
                    if (this.isProductPage) this._updateHistory();

                } else {
                    this.classList.toggle(CN_UNAVAILABLE, false);

                    const targetVariantExists = !!this._getVariantFromOptions(this.currentOptions);

                    if (!targetVariantExists || target.hasAttribute(ATTR_UNAVAILABLE)) {
                        this.classList.toggle(CN_UNAVAILABLE, true);
                        this.publish('variant:change', { unavailableSelection: true, options: [...this.currentOptions] });
                        this._setOptionsAvailability();

                        // Update history when selecting "None" option (empty value)
                        if (newValue === '' && this.isProductPage) {
                            this._updateHistory();
                        }
                        return;
                    }

                    this._updateVariantWithOption(target);
                    this._setOptionsAvailability();
                    this._publishChanges();
                    if (this.isProductPage) this._updateHistory();
                }

                if (this.variant) {
                    this.$variantIdInput.value = this.variant.id;
                    this.$variantIdInput.disabled = false;
                } else {
                    this.$variantIdInput.disabled = true;
                }
            } else if (e instanceof CustomEvent) {
                const targetInput = this._getInputEl(e.target.value, +e.target.getAttribute(ATTR_OPTION_INDEX));
                if (targetInput) this._updateDisplayValue(targetInput);
            }
        }

        _onSellingPlanChange({ sellingPlanId }) {
            this.sellingPlanId = sellingPlanId;
            this._publishChanges();
            if (this.isProductPage) this._updateHistory();
        }

        _updateDisplayValue(target) {
            if (!this.$displayValues || this.$displayValues.length === 0 || !target) return;
            const optionIndex = target.getAttribute(ATTR_OPTION_INDEX);
            if (optionIndex === null) return;

            const match = this.$displayValues.find(item => item.getAttribute(ATTR_OPTION_INDEX) === optionIndex);
            if (match) {
                match.innerText = target.value;
            }
        }

        async _publishChanges() {
            const cacheKey = `${this.variant?.id || 'default'}${this.sellingPlanId || ''}`;
            let cachedDoc = cache.get(cacheKey);
            if (!cachedDoc) {
                cachedDoc = await this._fetchVariantDOM();
            }

            this.publish('variant:change', {
                doc: cachedDoc,
                variantId: this.variant?.id,
                stock: this.variant?.available,
                available_to_add: this.variant?.available_to_add,
                variantImagePosition: this.variant?.featured_media?.position,
                sellingPlanId: this.sellingPlanId
            });
        }

        _updateHistory() {
            if (!this.variant) {
                // Remove variant from URL when no variant is selected
                window.history.replaceState({}, '', window.location.pathname);
                return;
            }
            const url = `${window.location.pathname}?${this.historyURLParams.toString()}`;
            window.history.replaceState({}, '', url);
        }

        async _fetchVariantDOM() {
            const url = this.requestURL;
            this.publish('variant:loading', true);
            try {
                const doc = await fetchHTML(url);
                this.publish('variant:loading', false);
                const cacheKey = `${this.variant?.id || 'default'}${this.sellingPlanId || ''}`;
                cache.set(cacheKey, doc);
                return doc;
            } catch (error) {
                console.error('_fetchVariantDOM: Error fetching variant HTML:', error);
                this.publish('variant:loading', false);
                const cacheKey = `${this.variant?.id || 'default'}${this.sellingPlanId || ''}`;
                cache.set(cacheKey, null);
                return null;
            }
        }

        _getVariantFromOptions(optionsArray) {
            if (!this.variants) return null;
            return this.variants.find(({ options }) => arrEq(options, optionsArray));
        }


        _updateVariantWithOption(target) {
            if (!this.variants || !this.currentOptions) {
                console.error('_updateVariantWithOption: Variants or currentOptions not initialized.');
                return;
            }
            const index = +target.getAttribute(ATTR_OPTION_INDEX);
            const value = target.value;

            let newVariant = this._getVariantFromOptions(this.currentOptions);

            if (newVariant) {
                this.variant = newVariant;
            } else if (this.smartSelect) {
                newVariant = this._getAvailableVariant(value, index);
                if (newVariant) {
                    this.variant = newVariant;
                    this.currentOptions = [...this.variant.options];
                } else {
                    this.variant = null;
                }
            } else {
                this.variant = null;
            }

            if (this.prevOptionIndex !== index) {
                this.prevOptionIndex = index;
            }
        }

        _getVariantFromValue(setValue, i) {
            if (!this.currentOptions || !this.variants) return null;

            let tempOptions = [...this.currentOptions];
            tempOptions[i] = setValue;
            const foundVariant = this.variants.find(({ options }) => arrEq(options, tempOptions));
            return foundVariant;
        }


        _getAvailableVariant(value, index) {
            if (!this.variants) return null;

            const matchedVariants = this.variants.filter(variant => variant.options[index] === value);

            if (matchedVariants.length === 0) return null;

            const availableMatches = matchedVariants.filter(v => v.available);

            let bestMatch = null;

            if (availableMatches.length > 0) {
                bestMatch = availableMatches.find(this._isMatchPrevOption.bind(this));
                if (!bestMatch) {
                    bestMatch = availableMatches[0];
                }
            } else {
                bestMatch = matchedVariants.find(this._isMatchPrevOption.bind(this));
                if (!bestMatch) {
                    bestMatch = matchedVariants[0];
                }
            }


            if (bestMatch) {
                this._selectVariantOptions(bestMatch, index);
            }

            return bestMatch;
        }

        _isMatchPrevOption(variant) {
            if (this.prevOptionIndex === undefined || this.prevOptionIndex === null || !this.variant || !this.variant.options) {
                return false;
            }
            const match = variant.options[this.prevOptionIndex] === this.variant.options[this.prevOptionIndex];
            return match;
        }

        _selectVariantOptions(srcVariant, ignoreIndex) {
            srcVariant.options.forEach((value, i) => {
                if (i !== ignoreIndex) {
                    const input = this._getInputEl(value, i);

                    if (input) {
                        if (input.type === 'radio' || input.type === 'checkbox') {
                            input.checked = true;
                        } else {
                            input.value = value;
                        }

                        input.dispatchEvent(new CustomEvent('change', {
                            bubbles: true,
                            cancelable: false
                        }));
                    }
                }
            });
        }

        _getOptionsState(acc, option, i) {
            if (!this.currentOptions) {
                console.error("_getOptionsState: currentOptions not initialized!");
                return acc;
            }
            option.values.forEach(value => {
                const matchedVariant = this._getVariantFromValue(value, i);
                acc.push({
                    value,
                    index: i,
                    exists: !!matchedVariant,
                    available: matchedVariant?.available
                });
            });
            return acc;
        }

        _getInputEl(value, index) {
            if (value === null || value === undefined || index === null || index === undefined) {
                return null;
            }
            const escapedValue = value.toString().replace(/["\\]/g, '\\$&');
            const selector = `[value="${escapedValue}"][${ATTR_OPTION_INDEX}="${index}"]`;
            const element = this.querySelector(selector);


            return element;
        }

        _toggleOptionAvailability({ value, index, exists, available }) {
            const escapedValue = value.toString().replace(/["\\]/g, '\\$&');
            const selector = `[value="${escapedValue}"][${ATTR_OPTION_INDEX}="${index}"]`;
            // Get ALL matching inputs (both dropdown and radio)
            const $inputs = this.querySelectorAll(selector);

            $inputs.forEach($input => {
                $input.classList.toggle(CN_UNAVAILABLE, !exists);
                $input.toggleAttribute(ATTR_UNAVAILABLE, !exists);
                $input.classList.toggle(CN_SOLD_OUT, exists && !available);
            });
        }

        // --- Getters ---

        get requestURL() {
            if (!this.variant) {
                return this.productURL || '';
            }
            const params = new URLSearchParams();
            if (this.sellingPlanId) params.set('selling_plan', this.sellingPlanId);
            params.set('variant', this.variant.id);
            params.set('section_id', this.sectionId);
            const url = `${this.productURL}?${params.toString()}`;
            return url;
        }

        get productURL() {
            return this.getAttribute(ATTR_PRODUCT_URL) || window.location.pathname;
        }

        get historyURLParams() {
            if (!this.variant) {
                return new URLSearchParams();
            }
            const params = new URLSearchParams({
                'variant': this.variant.id
            });
            if (this.sellingPlanId) {
                params.set('selling_plan', this.sellingPlanId);
            } else {
                params.delete('selling_plan');
            }
            return params;
        }

        get isProductPage() {
            return this.hasAttribute('product-page');
        }

        get smartSelect() {
            return this.hasAttribute(ATTR_SMART_SELECT);
        }

    });
})();
